import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../contexts/ConfigContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import { useAuth } from "../../contexts/AuthContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import Info from "../../components/atoms/ErrorComponent";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import {
  TITLE_MAP,
  SEQUENCE_MAP,
  INLINE_SEQUENCE_MAP,
  ALL_ISLANDS,
  WeatherKey,
} from "./IslandRegistry";
import { useTodayScores } from "../../hooks/useScoredToday";
import InlineQuestion from "../../components/organisms/InlineQuestion/InlineQuestion";
import {
  getApiFullSequence,
  FullSequenceResponse,
} from "../../services/questionnaire.service";
import { useFetch } from "../../hooks/useFetch";
import {
  defaultAnswerValueByType,
  formatAnswers,
  getAnswerValue,
} from "../../helper/answer";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";
import QuizIcon from "../../assets/MentalPlus/Icone-interrogation.svg";
import ExplicationIcon from "../../assets/MentalPlus/Icone-question.svg";
import SolutionIcon from "../../assets/MentalPlus/Icone-light.svg";

const hexToRgb = (hex: string) => {
  const cleaned = hex.replace("#", "");
  const bigint = parseInt(cleaned, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const CursorIcon = ({
  width = 28,
  height = 28,
}: {
  width?: number;
  height?: number;
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.05 17.65C20.4226 17.3706 20.725 17.0082 20.9333 16.5916C21.1416 16.1751 21.25 15.7157 21.25 15.25V4.25C21.25 3.45435 20.9339 2.69129 20.3713 2.12868C19.8087 1.56607 19.0456 1.25 18.25 1.25H6.25C5.45435 1.25 4.69129 1.56607 4.12868 2.12868C3.56607 2.69129 3.25 3.45435 3.25 4.25V15.25C3.25 15.7157 3.35844 16.1751 3.56672 16.5916C3.775 17.0082 4.07741 17.3706 4.45 17.65L10.45 22.15C10.9693 22.5395 11.6009 22.75 12.25 22.75C12.8991 22.75 13.5307 22.5395 14.05 22.15L20.05 17.65Z"
      fill="currentColor"
    />
  </svg>
);

const rgbToHex = (r: number, g: number, b: number) => {
  return `#${[r, g, b]
    .map((value) => Math.round(value).toString(16).padStart(2, "0"))
    .join("")}`;
};

const interpolateColor = (
  color1: string,
  color2: string,
  factor: number
) => {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = c1.r + (c2.r - c1.r) * factor;
  const g = c1.g + (c2.g - c1.g) * factor;
  const b = c1.b + (c2.b - c1.b) * factor;

  return rgbToHex(r, g, b);
};

const getCursorColorFromScore = (score: number) => {
  const clampedScore = Math.max(0, Math.min(score, 12));
  const percent = (clampedScore / 12) * 100;

  const stops = [
    { percent: 0, color: "#34C759" },
    { percent: 27, color: "#FFCE48" },
    { percent: 55, color: "#FF9800" },
    { percent: 78, color: "#EE353F" },
    { percent: 100, color: "#EE353F" },
  ];

  for (let i = 0; i < stops.length - 1; i++) {
    const start = stops[i];
    const end = stops[i + 1];

    if (percent >= start.percent && percent <= end.percent) {
      const localFactor =
        (percent - start.percent) / (end.percent - start.percent);

      return interpolateColor(start.color, end.color, localFactor);
    }
  }

  return "#EE353F";
};

const getQuizResult = (score: number) => {
  const dynamicColor = getCursorColorFromScore(score);

  if (score <= 2) {
    return {
      color: dynamicColor,
      message:
        "Tu ne sembles pas présenter de signe notable d’anxiété ou de dépression actuellement.",
      subMessage: "Continue à prendre soin de toi au quotidien.",
    };
  }

  if (score <= 5) {
    return {
      color: dynamicColor,
      message:
        "Tu sembles présenter des signes légers d’anxiété ou de dépression.",
      subMessage:
        "Tu peux consulter les explications et solutions proposées si tu en ressens le besoin.",
    };
  }

  if (score <= 8) {
    return {
      color: dynamicColor,
      message:
        "Tu sembles présenter des signes modérés d’anxiété ou de dépression.",
      subMessage:
        "Les ressources proposées peuvent t’aider à mieux comprendre ce que tu ressens.",
    };
  }

  return {
    color: dynamicColor,
    message:
      "Tu sembles présenter des signes importants d’anxiété ou de dépression.",
    subMessage:
      "N’hésite pas à consulter les solutions et à te rapprocher d’une personne de confiance ou d’un professionnel.",
  };
};

const DisplayIsland: React.FC = () => {
  const {
    sequences,
    loading,
    error,
    fullSequenceByUserSequenceId,
  } = useResonanceData();

  const history = useHistory();
  const { study_slug, title: key } = useParams<{
    study_slug: string;
    title: string;
  }>();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { study } = useConfig();
  const { user } = useAuth();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();
  const { hasScoreToday, todayScores } = useTodayScores();
  const { getTitle } = useIslandVisibility();

  const [animatedCursorPercent, setAnimatedCursorPercent] = useState(0);

  const displayTitle = (key ? getTitle(key) : "") || (key ? TITLE_MAP[key] : "") || key || "";

  useIonViewWillEnter(() => {
    if (!key) return;

    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: displayTitle,
      welcomePage: false,
    });
  });

  const inlineSeqId = key ? INLINE_SEQUENCE_MAP[key] : undefined;
  const inlineSeq = sequences?.find((s) => s.sequence_id === inlineSeqId);
  const hasInlineData = !!(inlineSeq && inlineSeq.components?.length);

  const userSequenceId = inlineSeq?.id ?? 0;
  const questionnaireId = inlineSeq?.components?.[0]?.questionnaire_id ?? 0;
  const componentId = inlineSeq?.components?.[0]?.id ?? 0;

  const cached = userSequenceId
    ? fullSequenceByUserSequenceId?.[userSequenceId]
    : undefined;

  const fetchEnabled = Boolean(
    !loading && inlineSeqId && hasInlineData && userSequenceId && !cached
  );

  const { data: fetched, isLoading, isError } = useFetch<FullSequenceResponse>(
    `inline-sequence:${userSequenceId}`,
    getApiFullSequence,
    {
      studyId: study.id,
      userId: user.id,
      sequenceId: userSequenceId,
      lang: language,
    },
    { enabled: fetchEnabled }
  );

  const data = cached ?? fetched;

  const inlineQuestionnaire = useMemo(() => {
    if (!data?.questionnaires?.length || !questionnaireId) return undefined;

    return data.questionnaires.find(
      (q) => q.id.toString() === questionnaireId.toString()
    );
  }, [data?.questionnaires, questionnaireId]);

  const firstQuestion = inlineQuestionnaire?.questions?.[0];

  const initialValue = useMemo(() => {
    if (!firstQuestion) return null;

    const formatted = formatAnswers(data?.answers?.answers ?? []);
    const previousAnswer = formatted.find(
      (a) => a.question_id === firstQuestion.id
    );

    if (previousAnswer) {
      return getAnswerValue(firstQuestion.type, previousAnswer);
    }

    return defaultAnswerValueByType[firstQuestion.type];
  }, [data?.answers?.answers, firstQuestion]);

  const def = key ? ALL_ISLANDS[key] : undefined;
  const iconSourceCfg = def
    ? def.iconFromInline
      ? def.inline ?? def.main
      : def.main
    : undefined;
  const iconSequenceId = iconSourceCfg?.sequenceId;

  const hasScore = !!iconSequenceId && hasScoreToday.has(iconSequenceId);
  const todaysScore =
    iconSequenceId != null ? todayScores.get(iconSequenceId) : undefined;

  const iconScale =
    iconSourceCfg?.islandScale?.mode === "sprite"
      ? iconSourceCfg.islandScale
      : undefined;

  let Icon = def?.NightComponent;

  if (def?.weatherSprites) {
    if (!hasScore) {
      Icon = def.weatherSprites.nuit ?? def.NightComponent;
    } else if (todaysScore != null && iconScale) {
      const spriteKey = iconScale.map(todaysScore) as WeatherKey;
      Icon = def.weatherSprites[spriteKey] ?? def.NightComponent;
    } else {
      Icon = def.weatherSprites.soleil ?? def.NightComponent;
    }
  }

  const scoreForDisplay =
    todaysScore !== undefined && todaysScore !== null ? todaysScore : 6;

  const quizResult = getQuizResult(scoreForDisplay);
  const cursorPercent = Math.max(
    0,
    Math.min((scoreForDisplay / 12) * 100, 100)
  );
  const cursorColor = getCursorColorFromScore(scoreForDisplay);

  useEffect(() => {
    setAnimatedCursorPercent(0);

    const timer = setTimeout(() => {
      setAnimatedCursorPercent(cursorPercent);
    }, 150);

    return () => clearTimeout(timer);
  }, [cursorPercent]);

  if (loading) return <ResonanceLoader />;

  if (error || !sequences?.length) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }

  if (!key) {
    return (
      <Info
        title={t("common_nodata_title")}
        description="Aucune île demandée."
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }

  if (!inlineSeqId) {
    return (
      <Info
        title={t("common_nodata_title")}
        description={`Aucun questionnaire associé à ${key}.`}
        actionText=""
      />
    );
  }

  if (!hasInlineData) {
    return <ResonanceLoader />;
  }

  if (!def || !Icon) {
    return (
      <Info
        title={t("common_nodata_title")}
        description={`Aucune configuration trouvée pour ${key}.`}
        actionText=""
      />
    );
  }

  const goToVideo = (contentType: "Explications" | "Solutions") =>
    history.push(`/${study_slug}/tabs/home/island/${key}/${contentType}`);

  const goToQuestionnaire = () => {
    const seqId = SEQUENCE_MAP[key];
    if (!seqId) return;

    const seq = sequences.find((s) => s.sequence_id === seqId);
    if (!seq) return;

    const nextComponent = seq.components.find((c) => !c.is_done);
    if (!nextComponent) return;

    history.push(
      `/${study_slug}/tabs/home/island/${key}/sequences/${seq.id}/components/${nextComponent.id}`
    );

    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: t("questionnaire_title"),
      welcomePage: false,
    });
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        {isLoading && !cached && <ResonanceLoader />}

        {isError && (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
            actionText=""
          />
        )}

        {firstQuestion && (
          <TopCardWrapper>
            <InlineQuestion
              question={firstQuestion}
              questionnaireId={questionnaireId}
              userSequenceId={userSequenceId}
              componentId={componentId}
              questionnaireData={inlineQuestionnaire}
              initialValue={initialValue}
              autoSubmit={true}
              cardMargin="0px"
            />
          </TopCardWrapper>
        )}

        <ButtonsColumn>
          <ActionCard large onClick={goToQuestionnaire} type="button">
            <LeftIcon>
              <CardIcon src={QuizIcon} alt="quiz" />
            </LeftIcon>
            <CardCenterText large>
              Quiz anxiété depression
            </CardCenterText>
            <RightArrow>›</RightArrow>
          </ActionCard>

          {quizResult && (
            <ResultCard>
              <ResultHeader>
                <ResultTitle>Résultat du quiz</ResultTitle>
                <HelpIcon>?</HelpIcon>
              </ResultHeader>

              <ResultBar>
                <CursorWrapper
                  style={{
                    left: `${animatedCursorPercent}%`,
                    color: cursorColor,
                  }}
                >
                  <CursorIcon width={28} height={28} />
                </CursorWrapper>
              </ResultBar>

              <ResultText>{quizResult.message}</ResultText>
              <ResultSubText>{quizResult.subMessage}</ResultSubText>
            </ResultCard>
          )}

          <SmallCardsGroup>
            <ActionCard onClick={() => goToVideo("Explications")} type="button">
              <LeftIcon>
                <CardIcon src={ExplicationIcon} alt="explication" />
              </LeftIcon>
              <CardCenterText>Explications</CardCenterText>
              <RightArrow>›</RightArrow>
            </ActionCard>

            <ActionCard onClick={() => goToVideo("Solutions")} type="button">
              <LeftIcon>
                <CardIcon src={SolutionIcon} alt="solution" />
              </LeftIcon>
              <CardCenterText>Que faire ?</CardCenterText>
              <RightArrow>›</RightArrow>
            </ActionCard>
          </SmallCardsGroup>
        </ButtonsColumn>
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default DisplayIsland;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding-top: 10px;
  padding-bottom: 10px;
  gap: 18px;
`;

const TopCardWrapper = styled.div`
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
`;

const CardIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const ButtonsColumn = styled.div`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ActionCard = styled.button<{ large?: boolean }>`
  width: 100%;
  min-height: ${({ large }) => (large ? "88px" : "68px")};
  background: #ffffff;
  border: none;
  border-radius: 24px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 2px 2px 4px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  position: relative;

  &:active {
    transform: scale(0.98);
  }
`;

const LeftIcon = styled.div`
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const CardCenterText = styled.div<{ large?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ large }) => (large ? "1.05rem" : "0.95rem")};
  font-weight: 700;
  color: #111;
  text-align: center;
`;

const RightArrow = styled.div`
  font-size: 3.2rem;
  line-height: 1;
  color: #4285f4;
  flex-shrink: 0;
  z-index: 1;
  transform: translateY(-2px);
`;

const SmallCardsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
`;

const ResultCard = styled.div`
  width: 100%;
  max-width: 340px;
  background: white;
  border-radius: 24px;
  padding: 18px;
  box-shadow: 2px 2px 4px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeInUp 0.45s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111;
`;

const HelpIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #4285f4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const ResultBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 10px;
  background: linear-gradient(
    to right,
    #34c759 0%,
    #ffce48 27%,
    #ff9800 55%,
    #ee353f 78%
  );
  position: relative;
  margin-top: 14px;
`;

const CursorWrapper = styled.div`
  position: absolute;
  top: -30px;
  transform: translateX(-50%);
  transition: left 0.9s cubic-bezier(0.22, 1, 0.36, 1), color 0.35s ease;
  pointer-events: none;
  z-index: 2;

  svg {
    width: 28px;
    height: 28px;
    display: block;
    transition: fill 0.35s ease, stroke 0.35s ease, transform 0.25s ease;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12));
  }

  svg * {
    fill: currentColor !important;
    stroke: currentColor !important;
  }
`;

const ResultText = styled.div`
  font-size: 0.95rem;
  color: #333;
  line-height: 1.4;
`;

const ResultSubText = styled.div`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
`;